import { Skeleton } from '@/components/ui/skeleton'
import useGetMonthToDateScore from '@/services/api/cash-flow/useGetMonthToDateScore'
import CardErrorState from '@/pages/dashboard/widgets/CardErrorState'
import BannerSection from '@/pages/dashboard/widgets/BannerSection'

const TONE_CHIP_CLASS: Record<string, string> = {
  positive: 'bg-green-tr-12 text-[hsl(138,71%,23%)]',
  neutral: 'bg-accent-tr-12 text-txt-accent',
  caution: 'bg-yellow-tr-12 text-[hsl(40,100%,30%)]',
  negative: 'bg-red-tr-12 text-[hsl(356,72%,47%)]',
}

/**
 * Month-to-date forecast score — how the month currently open is tracking,
 * against the last full close.
 *
 * The score is over the elapsed slice only (see MonthToDateScoreService), so
 * the "N of M days in" line isn't decoration: it's the caveat that makes the
 * number honest early in a month.
 */
export default function MonthToDateCard() {
  const { data, status, refetch } = useGetMonthToDateScore()
  const mtd = data?.data

  const chipClass =
    TONE_CHIP_CLASS[mtd?.band?.tone ?? 'neutral'] ?? TONE_CHIP_CLASS.neutral

  const delta = mtd?.delta ?? null
  const isDown = delta !== null && delta < 0

  return (
    <BannerSection
      label={mtd ? `${mtd.month} to date` : 'This month to date'}
      right={
        status === 'success' &&
        mtd?.band && (
          <span
            className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[11.5px] font-bold ${chipClass}`}
          >
            {mtd.band.label}
          </span>
        )
      }
    >
      {status === 'pending' && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-4 w-48" />
        </div>
      )}

      {status === 'error' && <CardErrorState onRetry={() => refetch()} />}

      {status === 'success' && mtd && mtd.score === null && (
        <div className="flex flex-col gap-1">
          <div className="font-raleway text-[32px] font-bold leading-none text-txt-secondary tabular-nums">
            —
          </div>
          <div className="text-[12.5px] text-txt-secondary">
            Nothing recorded yet this month.
          </div>
        </div>
      )}

      {status === 'success' && mtd && mtd.score !== null && (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-end gap-2.5">
            <div className="font-raleway text-[32px] font-bold leading-none tabular-nums text-txt-primary tablet:text-[38px]">
              {mtd.score}
            </div>
            {delta !== null && (
              <div
                className={`flex items-center gap-1 pb-1 text-[12.5px] font-semibold ${
                  isDown ? 'text-[hsl(356,72%,47%)]' : 'text-[hsl(138,71%,23%)]'
                }`}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  {isDown ? (
                    <path
                      d="M6 2v8M6 10l-3.6-3.6M6 10l3.6-3.6"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <path
                      d="M6 10V2M6 2L2.4 5.6M6 2l3.6 3.6"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
                {Math.abs(delta)} on {mtd.last_closed?.month}&apos;s close
              </div>
            )}
          </div>

          <div className="text-[12.5px] leading-normal text-txt-secondary">
            {mtd.days_elapsed} of {mtd.days_total} days in
            {mtd.categories_in_play > 0 && (
              <>
                {' · '}
                {mtd.categories_tracked} of {mtd.categories_in_play} categories tracked
              </>
            )}
          </div>
        </div>
      )}
    </BannerSection>
  )
}
